/* eslint-disable prettier/prettier */
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import FiberNewIcon from '@mui/icons-material/FiberNew';
import { IAugmentedAuditLog } from 'features/dashboard/types';
import { axiosPrivate } from 'shared/api';
import toast from 'shared/utils/toast';

const RELEVANT_FIELDS = ['status'];

/**
 * Configuration payload options required to initialize an explicit instance of BaseService.
 */
export interface ServiceConfig {
  /** The root endpoint prefix endpoint targeting the Django/backend resource module (e.g., "equipments"). */
  modulePrefix: string;
  /** Restricts or permits discrete row-level execution deletion handling on the engine instance. */
  canDelete?: boolean;
  /** Restricts or permits cluster purging or mass-row structural resource truncations. */
  canBulkDelete?: boolean;
}

/**
 * Enterprise Abstract Base Service Class Engine.
 * Standardizes backend JSON/REST network orchestration routines including transactional CRUD steps, 
 * automated historical audit mapping, file transmission mutations, and predictive HTTP fault extraction.
 * 
 * @template T - Structural domain data entity model context inheriting a unique operational identity `id`.
 */
export class BaseService<T extends { id: number | string }> {
  modulePrefix = 'base';
  canDelete = false;
  canBulkDelete = false;

  // FIRMA (Legacy): Accetta solo la stringa del prefisso
  constructor(prefix: string);

  // FIRMA (Nuova): Accetta l'oggetto di configurazione completo
  constructor(config: ServiceConfig);

  // IMPLEMENTAZIONE UNICA: Gestisce entrambi i casi a runtime
  constructor(configOrPrefix: string | ServiceConfig) {
    if (typeof configOrPrefix === 'string') {
      this.modulePrefix = configOrPrefix;
    } else {
      this.modulePrefix = configOrPrefix.modulePrefix;
      this.canDelete = configOrPrefix.canDelete ?? this.canDelete;
      this.canBulkDelete = configOrPrefix.canBulkDelete ?? this.canBulkDelete;
    }
  }

  /**
   * Fetches an offset-paginated dataset block index containing model entities.
   * 
   * @param limit - Threshold defining the maximum query row return count block slice.
   * @param offset - Pagination skip index anchor.
   * @param params - Auxiliary filter criteria parameters structured into Key-Value entries.
   * @returns Resolves the raw backend paginated payload matrix interface.
   * @throws Rethrows parsed Axios faults to shield the parent UI lifecycle.
   */
  public async getAllPaginated(limit = 0, offset = 0, params: Record<string, unknown> = {}) {
    try {
      const res = await axiosPrivate.get(`${this.modulePrefix}/`, {
        params: { limit, offset, ...params }
      });
      return res.data;
    } catch (e) {
      toast.apiError(e);
      throw e;
    }
  }

  /**
   * Fetches lightweight identity summary mappings for populating dropdown choice selectors.
   * Concatenates entity identifying identifiers into friendly descriptor strings.
   * 
   * @param params - Optional parameter filters.
   */
  public async getAllBaseInformation(params: Record<string, unknown> = {}) {
    try {
      const res = await axiosPrivate.get(`${this.modulePrefix}-summary/`, { params });
      return res.data.map((entity) => ({
        ...entity,
        description: `${entity.code} - ${entity.description}`
      }));
    } catch (e) {
      toast.apiError(e);
      throw e;
    }
  }

  /**
   * Retrieves a granularly detailed singular record item by its unique resource key identifier.
     TODO: public async get(id: T['id']): Promise<T> { 
  */
  public async get(id: number | string) {
    try {
      const res = await axiosPrivate.get(`${this.modulePrefix}/${id}/`);
      return res.data;
    } catch (e) {
      toast.apiError(e);
      throw e;
    }
  }

  /**
   * Dispatches a DELETE operation targeting a discrete unique resource record instance.
   */
  public async delete(id: number) {
    try {
      const res = await axiosPrivate.delete(`${this.modulePrefix}/${id}/`);
      return res;
    } catch (e) {
      toast.apiError(e);
      throw e;
    }
  }

  /**
   * Creates a new entity using a payload that excludes the persisted entity ID.
   *
   * The server-generated ID is returned as part of the created entity.
   * TODO:  public async add(entity: Omit<T, 'id'>): Promise<T>  {
   * @param entity - Entity creation payload without the `id` field.
   * @returns The newly created entity including its persisted ID.
   */
  public async add(entity: T): Promise<T>  {
    try {
      const res = await axiosPrivate.post(`${this.modulePrefix}/`, entity);
      return res.data;
    } catch (error) {
        toast.apiError(error);
        throw error;
      }
      
  }

  /**
   * Partially updates an existing entity using a PATCH request.
   *
   * The entity ID is provided separately and cannot be included in the
   * update payload. Only fields other than `id` may be updated, and all
   * payload fields are optional.
   * TODO: ublic async update(
        id: T['id'],
        entity: Partial<Omit<T, 'id'>>
      ): Promise<T>
   * @param id - Unique identifier of the entity to update.
   * @param entity - Partial update payload without the `id` field.
   * @returns The updated entity.
   */
  public async update(id: number | string, entity: T | any) {
    try {
      const res = await axiosPrivate.patch(`${this.modulePrefix}/${id}/`, entity);
      return res.data;
    } catch (error) {
      toast.apiError(error);
      throw error;
    }
  }

  /**
   * Triggers a mass destructive purge endpoint routing block if explicitly supported by instance settings.
   */
  public async bulkDelete() {
    try {
      if (!this.canBulkDelete) {
        throw new Error("Bulk deletion capabilities are restricted on this service controller instance configuration.");
      }
      const res = await axiosPrivate.post(`${this.modulePrefix}/purge/`);
      return res.data;
    } catch (e: any) {
      toast.apiError(e);
      throw e;
    }
  }

  /**
   * Dispatches an HTTP OPTIONS metadata inspection call mapping server descriptor parameters schemas.
   */
  public async options() {
    try {
      const res = await axiosPrivate.options(`${this.modulePrefix}/`);
      return res.data;
    } catch (e: any) {
      toast.apiError(e);
      throw e;
    }
  }

  /**
   * Fetches historical logging streams tracking alterations, mapping custom semantic visual UI indicators.
   */
  public async history(id: number | string): Promise<IAugmentedAuditLog[]> {
    const res = await axiosPrivate.get(`${this.modulePrefix}/${id}/history/`);
    return res.data.map((d: IAugmentedAuditLog) => {
      d.iconBackground = 'bg-primary-500';

      if (d && RELEVANT_FIELDS.some((field) => d.changes.includes(field))) {
        d.iconBackground = 'bg-red-500';
      }

      switch (d.action) {
        case 0:
          d.iconBackground = 'bg-green-500';
          d.icon = FiberNewIcon;
          break;
        case 1:
          d.icon = EditIcon;
          break;
        case 2:
        default:
          d.iconBackground = 'bg-red-500';
          d.icon = DeleteIcon;
          break;
      }
      return d;
    });
  }

  /**
   * Encapsulates standard Multi-part FormData structural bindings to stream binary file assets up to the server.
   */
  attachFiles = async (attachment: File, attachmentName: string, entityId: number) => {
    const res = await axiosPrivate.postForm(`${this.modulePrefix}/${entityId}/attach-files/`, {
      [attachmentName]: attachment
    });
    return res.data;
  };

  /**
   * Downloads a targeted file stream using an integrated binary Blob parsing pipeline anchor element shortcut.
   */
  downloadAttachment = async (entityId: number, attachmentName: string) => {
    axiosPrivate
      .get(`${this.modulePrefix}/${entityId}/download-attachment/${attachmentName}/`, {
        responseType: 'blob'
      })
      .then((res) => {
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const contentDisposition = res.headers['content-disposition'];
        if (contentDisposition?.indexOf('attachment') !== -1) {
          const filenameMatches = contentDisposition.match(
            /(?:.*filename\*|filename)=(?:([^'"]*)''|("))([^;]+)\2(?:[;`\n]|$)/
          );
          const filename = filenameMatches.pop();
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', filename);
          document.body.appendChild(link);
          link.click();
        }
      });
  };

  /**
   * Destroys an isolated target attached binary element associated with an active entity workspace tracking root.
   */
  deleteAttachment = async (entityId: number, attachmentName: string) => {
    const res = await axiosPrivate.post(
      `${this.modulePrefix}/${entityId}/delete-attachment/${attachmentName}/`
    );
    return res.data;
  };

  
  /**
   * Streams a binary spreadsheet template layout document allowing mass import tasks mapping layout rules.
   */
  getImportTemplate = async () => {
    axiosPrivate
      .get(`${this.modulePrefix}/import_template/`, {
        responseType: 'blob'
      })
      .then((res) => {
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const contentDisposition = res.headers['content-disposition'];
        if (contentDisposition?.indexOf('attachment') !== -1) {
          const filenameMatches = contentDisposition.match(
            /(?:.*filename\*|filename)=(?:([^'"]*)''|("))([^;]+)\2(?:[;`\n]|$)/
          );
          const filename = filenameMatches.pop();
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', filename);
          document.body.appendChild(link);
          link.click();
        }
      });
  };
}
